// LessonScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { learningAPI } from '../services/api';
import { useProfile } from '../context/ProfileContext';

export default function LessonScreen() {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const { profile, refreshProfile } = useProfile();
  const [module, setModule] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    learningAPI.getModule(moduleId)
      .then(m => { setModule(m); setLoading(false); })
      .catch(() => navigate('/learning'));
  }, [moduleId]);

  const lesson = module?.lessons?.[currentLesson];

  const handleComplete = async () => {
    if (!profile || !lesson) return;
    try {
      await learningAPI.saveProgress({
        profileId: profile.id,
        lessonId: lesson.id,
        moduleId,
        score: 100,
        completed: true,
        timeSpent: 60
      });
      refreshProfile();
      setCompleted(true);
    } catch (e) { console.error(e); }
  };

  const handleQuiz = (optionIndex) => {
    if (quizAnswer !== null) return;
    setQuizAnswer(optionIndex);
    setQuizResult(optionIndex === lesson.content.questions[0].answer);
  };

  const nextLesson = () => {
    if (currentLesson < module.lessons.length - 1) {
      setCurrentLesson(i => i + 1);
      setQuizAnswer(null);
      setQuizResult(null);
      setCompleted(false);
    } else {
      navigate('/learning');
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #26de81, #20bf6b)', paddingBottom: 16 }}>
        <button className="btn-back" onClick={() => navigate('/learning')} style={{ background: 'rgba(255,255,255,0.3)', color: 'white' }}>←</button>
        <div>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.2rem' }}>{module?.icon} {module?.name}</h1>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>Lesson {currentLesson + 1} of {module?.lessons?.length}</div>
        </div>
      </div>

      <div className="screen-body" style={{ paddingTop: 20 }}>
        {lesson && (
          <div>
            <h2 style={{ fontFamily: 'var(--font-title)', fontSize: '1.3rem', marginBottom: 16 }}>{lesson.title}</h2>

            {lesson.content.type === 'info' && (
              <div>
                <div className="card" style={{ background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)', marginBottom: 16 }}>
                  <p style={{ lineHeight: 1.8, fontSize: '1rem' }}>{lesson.content.text}</p>
                </div>
                <div className="grid-2">
                  {lesson.content.examples?.map((ex, i) => (
                    <div key={i} className="card" style={{ textAlign: 'center', fontSize: '1.5rem', padding: 16 }}>{ex}</div>
                  ))}
                </div>
                {!completed ? (
                  <button className="btn btn-success btn-full btn-lg" style={{ marginTop: 20 }} onClick={handleComplete}>
                    ✅ I Learned This! (+8 ⭐)
                  </button>
                ) : (
                  <button className="btn btn-primary btn-full btn-lg" style={{ marginTop: 20 }} onClick={nextLesson}>
                    Next Lesson →
                  </button>
                )}
              </div>
            )}

            {lesson.content.type === 'quiz' && (
              <div>
                <div className="card" style={{ background: '#FFF9E6', border: '2px solid var(--yellow)', marginBottom: 16 }}>
                  <p style={{ fontWeight: 800, fontSize: '1.1rem' }}>{lesson.content.questions[0].q}</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {lesson.content.questions[0].options.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuiz(i)}
                      style={{
                        padding: '14px 20px', border: '3px solid',
                        borderColor: quizAnswer === null ? 'var(--border)' :
                          i === lesson.content.questions[0].answer ? 'var(--green)' :
                          quizAnswer === i ? 'var(--red)' : 'var(--border)',
                        borderRadius: 16, background:
                          quizAnswer === null ? 'white' :
                          i === lesson.content.questions[0].answer ? '#E8F5E9' :
                          quizAnswer === i ? '#FFEBEE' : 'white',
                        cursor: quizAnswer !== null ? 'default' : 'pointer',
                        fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '1rem',
                        textAlign: 'left', transition: 'all 0.2s'
                      }}
                    >
                      {opt} {quizAnswer !== null && i === lesson.content.questions[0].answer ? ' ✅' : ''}
                    </button>
                  ))}
                </div>
                {quizResult !== null && (
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', marginBottom: 8 }}>{quizResult ? '🎉' : '🤔'}</div>
                    <p style={{ fontWeight: 800 }}>{quizResult ? 'Correct! Amazing!' : 'Not quite, but keep learning!'}</p>
                    <button className="btn btn-primary btn-full" style={{ marginTop: 14 }} onClick={async () => { await handleComplete(); nextLesson(); }}>
                      Continue →
                    </button>
                  </div>
                )}
              </div>
            )}

            {lesson.content.type === 'vocabulary' && (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {lesson.content.words.map((word, i) => (
                    <div key={i} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ fontSize: '2rem' }}>{word.emoji}</div>
                      <div>
                        <div style={{ fontWeight: 800 }}>{word.english}</div>
                        <div style={{ color: 'var(--purple)', fontWeight: 700, fontSize: '1.1rem' }}>{word.translation}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {!completed ? (
                  <button className="btn btn-success btn-full btn-lg" style={{ marginTop: 20 }} onClick={handleComplete}>
                    ✅ I Know These! (+8 ⭐)
                  </button>
                ) : (
                  <button className="btn btn-primary btn-full btn-lg" style={{ marginTop: 20 }} onClick={nextLesson}>
                    Next Lesson →
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
